// AccentLengthUnbound.js
import React from 'react';
import { Alert } from 'reactstrap';
import PropTypes from 'prop-types';
import { AccentLengthMetricUnbound } from './AccentLengthMetricUnbound';
import { AccentLengthImperialUnbound } from './AccentLengthImperialUnbound';
import { denominators, fromDecimal, getMeasureValueUnitTypes, isMeasuredValue, toLength } from '../products/ProductConfiguratorShared';
import { accentUtils, from } from '../../services/HelperService';



class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render shows the fallback UI.
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error if needed.
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <Alert color="danger">
                    <h5>Something went wrong.</h5>
                    <p>{this.state.error?.message}</p>
                </Alert>
            );
        }

        return this.props.children;
    }
}


const AccentLengthUnboundInternal = (props) => {
    const { defaultUnit, precision } = props;

    //could be an enum or string or number
    const defaultUnitID = accentUtils.isNull(defaultUnit) ? 10 : Number(isNaN(defaultUnit) ? Number(defaultUnit.toString()) : defaultUnit);

    const defaultLength = isMeasuredValue(props.defaultValue) ? props.defaultValue : isNaN(Number(props.defaultValue)) ? undefined : toLength(Number(props.defaultValue), defaultUnit ?? defaultUnitID);

    // Determine if the unit is metric using the provided helper function
    const unitTypes = getMeasureValueUnitTypes();
    const currentUnit = unitTypes.find(ut => ut.ID === defaultUnitID);
    const isMetric = currentUnit ? currentUnit.IsMetric : true;

    const step = fromDecimal(precision);
    const value = fromDecimal(defaultLength?.Value ?? 0);


    const onChange = e => {
        props.onChange(e);
    };

    const onBlur = e => {

        if (props.onBlur)
            props.onBlur(e);
    };

    const onFocus = e => {

        if (props.onFocus)
            props.onFocus(e);
    };


    const precisionDemoninator = from(denominators).firstOrDefault(x => x.ID == step)?.Value;

    // Render the appropriate component based on the unit type
    return isMetric ? (
        <AccentLengthMetricUnbound {...props} step={step} defaultValue={value} onChange={onChange} onBlur={onBlur} onFocus={onFocus} defaultUnit={defaultUnitID} />
    ) : (
            <AccentLengthImperialUnbound {...props} defaultValue={value} onChange={onChange} onBlur={onBlur} onFocus={onFocus} precision={precisionDemoninator} defaultUnit={defaultUnitID} />
    );
};

export const AccentLengthUnbound = (props) => {
    return (
        <ErrorBoundary>
            <AccentLengthUnboundInternal {...props} />
        </ErrorBoundary>
    );
};

AccentLengthUnbound.propTypes = {
    defaultUnit: PropTypes.oneOf([
        10,
        100,
        10000,
        254,
        3048,
        9144
    ]).isRequired,
    defaultValue: PropTypes.object, // Expected structure based on your helper functions
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    min: PropTypes.number,
    max: PropTypes.number,
    debounceMilliseconds: PropTypes.number,
    className: PropTypes.string,
    label: PropTypes.string,
    required: PropTypes.bool,
    invalid: PropTypes.bool,
    disabled: PropTypes.bool,
    placeHolder: PropTypes.string,
    precision: PropTypes.number,
    dataTagName: PropTypes.string,
    // The accessible name for the measurement when there is no visible label prop
    // (the configurator labels sizes with its own header) — each part input gets
    // "<ariaLabel> inches" / "<ariaLabel> fraction" etc.
    ariaLabel: PropTypes.string
};


